import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight, Sparkles } from "lucide-react";
import Aurora from "@/components/Aurora";
import { getCheckoutStatus } from "@/lib/api";

const MAX_ATTEMPTS = 12;
const POLL_INTERVAL_MS = 2500;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();

  const [status, setStatus] = useState(() => (sessionId ? "polling" : "error"));
  const [data, setData] = useState(null);
  const [message, setMessage] = useState(() =>
    sessionId ? "Verifying your payment with Stripe…" : "Missing checkout session."
  );
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const resp = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setData(resp);

        if (resp.payment_status === "paid") {
          setStatus("paid");
          setMessage("Payment confirmed.");
          if (resp.account_created) {
            // Account already created (returning user) — send to login
            setTimeout(() => navigate("/login", { replace: true }), 1500);
          } else {
            setTimeout(() => navigate(`/signup?session_id=${sessionId}`, { replace: true }), 1200);
          }
          return;
        }
        if (resp.status === "expired" || resp.payment_status === "unpaid") {
          if (attemptsRef.current >= MAX_ATTEMPTS) {
            setStatus("failed");
            setMessage("Payment was not completed. You can try again from the landing page.");
            return;
          }
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("error");
          setMessage("Still waiting on Stripe. Refresh this page in a moment.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("error");
          setMessage("Couldn't verify payment. Please contact support@favorstudio.agency.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden grid place-items-center px-5 py-16">
      <Aurora variant="intense" rings />
      <div className="relative max-w-xl w-full">
        <div className="flex items-center gap-2 justify-center">
          <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center shadow-[0_0_24px_rgba(0,229,255,0.5)]">
            <Sparkles className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-display text-white text-lg">
            <span className="font-normal">Favor</span>{" "}
            <span className="font-bold">Studio.</span>
          </span>
        </div>

        <div className="glass-card p-8 sm:p-10 mt-8 text-center" data-testid="checkout-success-panel">
          {status === "polling" && (
            <>
              <Loader2 className="w-10 h-10 text-cyan-300 mx-auto animate-spin" />
              <h1 className="font-display text-3xl sm:text-4xl mt-6">
                <span className="font-normal">Confirming Your </span>
                <span className="font-bold">Purchase.</span>
              </h1>
              <p className="mt-4 text-zinc-400 text-sm leading-relaxed">{message}</p>
              <p className="mt-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                Do not close this window
              </p>
            </>
          )}

          {status === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center shadow-[0_0_40px_rgba(0,229,255,0.7)]">
                <CheckCircle2 className="w-8 h-8 text-black" strokeWidth={2.5} />
              </div>
              <h1 className="font-display text-3xl sm:text-4xl mt-6">
                <span className="font-normal">Payment </span>
                <span className="font-bold">Confirmed.</span>
              </h1>
              <p className="mt-4 text-zinc-300 text-sm leading-relaxed">
                Redirecting you to create your account…
              </p>
              {data && (
                <p className="mt-3 text-[11px] tracking-[0.25em] uppercase text-zinc-500">
                  ${ (data.amount_total / 100).toFixed(2) } { (data.currency || "usd").toUpperCase() } &middot; {sessionId.slice(0, 14)}…
                </p>
              )}
            </>
          )}

          {status === "failed" && (
            <>
              <AlertTriangle className="w-10 h-10 text-amber-300 mx-auto" />
              <h1 className="font-display text-3xl sm:text-4xl mt-6">
                <span className="font-normal">Payment Not </span>
                <span className="font-bold">Completed.</span>
              </h1>
              <p className="mt-4 text-zinc-400 text-sm leading-relaxed">{message}</p>
              <Link to="/" className="btn-aurora mt-7 inline-flex">
                Back to landing page <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <AlertTriangle className="w-10 h-10 text-rose-300 mx-auto" />
              <h1 className="font-display text-3xl sm:text-4xl mt-6">
                <span className="font-normal">Hmm — </span>
                <span className="font-bold">Try Again.</span>
              </h1>
              <p className="mt-4 text-zinc-400 text-sm leading-relaxed">{message}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-aurora mt-7"
                data-testid="checkout-retry-button"
              >
                Retry verification <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
